/**
 * CoachObservationsPanel — shared renderer for any list of Observation
 * objects produced by the coach engine.
 *
 * Used by:
 *   - SetupsTab (per-setup analysis card)
 *   - AtmTab (planned, per-ATM card)
 *   - Anywhere else we want to surface filtered observations without
 *     re-implementing the card layout
 *
 * Different from LiveCoachFeed:
 *   - LiveCoachFeed orchestrates DAILY + GROUPS + 30D-TRENDS sections,
 *     handles onboarding + first-insight celebration, owns refresh state.
 *   - This panel is a pure renderer: it takes a pre-computed observation
 *     array, an empty-state message, and renders the cards.
 *
 * Props:
 *   observations  Array<Observation>  — pre-filtered/computed
 *   title         string?              — optional header text
 *   emptyMessage  string?              — shown when array is empty
 *   compact       bool                 — tighter spacing, suitable for inline use
 *   darkMode      bool
 */

import { Sparkles, History, RotateCcw } from 'lucide-react';
import { SEVERITY_STYLE } from '../../services/coach';
import { temporalBadge, TEMPORAL_STATE } from '../../services/coach/synthesis/temporalContext';
import ObservationActions from './ObservationActions';

const ObservationCard = ({ obs, darkMode, compact, onAction }) => {
  const style = SEVERITY_STYLE[obs.severity] || SEVERITY_STYLE.info;
  // Profile-aware temporal badge — surfaces when this observation has been
  // firing for multiple snapshots, or has returned after being resolved.
  // Renders as a small chip next to the category label so the trader sees
  // at a glance whether Coach has been watching this for a while.
  const badge = temporalBadge(obs.temporalContext);
  const isReturning = obs.temporalContext?.state === TEMPORAL_STATE.RETURNING;
  return (
    <div
      className={`
        rounded-md border-l-2 ${style.border}
        ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}
        ${compact ? 'p-2' : 'p-2.5'}
      `}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`w-1.5 h-1.5 rounded-full ${style.dot} shrink-0`} />
        <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'} truncate`}>
          {obs.category || obs.scope}
        </span>
        {badge && (
          <span
            title={isReturning
              ? 'This pattern was previously resolved and has returned'
              : `Coach has been watching this for ${obs.temporalContext.runs} snapshots`}
            className={`inline-flex items-center gap-0.5 text-[9px] px-1 py-px rounded-full border ${
              isReturning
                ? (darkMode ? 'bg-amber-500/15 border-amber-500/30 text-amber-200' : 'bg-amber-100 border-amber-300 text-amber-700')
                : (darkMode ? 'bg-purple-500/15 border-purple-500/30 text-purple-200' : 'bg-purple-100 border-purple-300 text-purple-700')
            }`}
          >
            {isReturning ? <RotateCcw size={8} /> : <History size={8} />}
            {badge}
          </span>
        )}
      </div>
      <p className={`text-sm font-medium leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {obs.title}
      </p>
      {obs.detail && (
        <p className={`text-xs leading-snug mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {obs.detail}
        </p>
      )}
      {obs.action && (
        <p className={`text-[11px] italic leading-snug mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          → {obs.action}
        </p>
      )}
      {Array.isArray(obs.actions) && obs.actions.length > 0 && onAction && (
        <ObservationActions
          observation={obs}
          onAction={onAction}
          darkMode={darkMode}
          compact={compact}
        />
      )}
    </div>
  );
};

const CoachObservationsPanel = ({
  observations = [],
  title,
  emptyMessage,
  compact = false,
  darkMode = true,
  onAction,
}) => {
  const hasObservations = Array.isArray(observations) && observations.length > 0;

  return (
    <div className={`space-y-2 ${compact ? '' : 'mb-2'}`}>
      {title && (
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={12} className="text-purple-400 shrink-0" />
          <h4 className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {title} {hasObservations && `· ${observations.length}`}
          </h4>
        </div>
      )}
      {hasObservations ? (
        observations.map((obs, i) => (
          <ObservationCard
            key={obs.id || `obs-${i}`}
            obs={obs}
            darkMode={darkMode}
            compact={compact}
            onAction={onAction}
          />
        ))
      ) : (
        <div className={`rounded-md border border-dashed p-3 text-center ${
          darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-300 text-gray-400'
        }`}>
          <Sparkles size={14} className="mx-auto mb-1 opacity-60" />
          <p className="text-xs leading-snug">
            {emptyMessage || 'No observations yet — the Coach needs more data here.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default CoachObservationsPanel;
